import React from "react";
import clsx from "clsx";
import { shallowEqual } from "react-redux";
import styles from "./Summary.module.scss";
import Text from "../../../../../../shared-components/css-modules/toyota/atoms/Text";
import { CarConfigLabel, useCarConfigOptionalLabel } from "../../../../../utils/labelUtils";
import Button from "../../../../../../shared-components/css-modules/toyota/atoms/Button";
import { trackOrderOverviewFinanceTerms } from "../../../../../utils/tracking";
import { useCarConfigSelector } from "../../../../../redux/store";
import { AemFmComponent } from "../../../../../../../../common-deprecated/utils/aemFlexibilityMatrixUtils";
import { getAemFlexibilityMatrixConfig } from "../../../../../../../../common-deprecated/settings/utils/financeSettingUtilsAem";
import { useTabsContext } from "./SummaryTabsContext";
import type { SummaryTabPropsTypes } from "../../../../../hooks/useAphSummaryTabs";
import type { FormatMonthlyPriceType } from "../../../../../../../../common-deprecated/priceUtils";
import { financeCtaSelector, pricingDetailsSelector } from "../../../../../utils/selectorUtils";
import { MODAL_FINANCE_CALCULATOR, MODAL_FINANCE_DISCLAIMER } from "../../../../../utils/modalConstants";
import useAphModal from "../../../../../hooks/useAphModal";
import Price from "../../../common/price/Price";
import { formatMonthlyFinancePrice } from "../../../../../../shared-logic/utils/priceUtils";
import { CarConfigConfigureSection } from "../../../../../utils/types/SectionTypes";
import Icon from "../../../../../../shared-components/css-modules/toyota/atoms/Icon";
import SummaryServices from "./SummaryServices";

const SummaryFinance = (props: SummaryTabPropsTypes): JSX.Element | null => {
    const { tabId, tab } = props;
    const { commonSettings, info, total, basket, modelCode } = useCarConfigSelector(financeCtaSelector, shallowEqual);
    const { showFinanceDisclaimerInline } = useCarConfigSelector(pricingDetailsSelector, shallowEqual);
    const financeQuoteId = useCarConfigSelector((state) => state.finance.quotes.finance);
    const price = { ...basket.total, monthly: Number(total) };

    const priceFormat =
        getAemFlexibilityMatrixConfig(commonSettings, AemFmComponent.Carconfig, "new", modelCode)?.config?.finance
            ?.format || "";

    const monthlyRateItems = formatMonthlyFinancePrice<FormatMonthlyPriceType>(
        price,
        info,
        commonSettings,
        priceFormat,
        false,
        price.monthly,
    );
    const tabsContext = useTabsContext();
    const { showModalHelper } = useAphModal();
    const carConfigFinanceInfo = useCarConfigOptionalLabel("carConfigFinanceInfo");

    return (
        <section
            role="tabpanel"
            id={tabId}
            aria-labelledby={tab}
            className={clsx(styles["tab-panel"], styles.finance)}
            hidden={tabsContext.openTab !== "finance"}
        >
            <ul role="list" className={styles["config-sublist"]}>
                <li className={styles["config-subitem"]}>
                    <Text tag="h3" variant="apheleia-h4">
                        <CarConfigLabel label="carConfigMonthlyPurchase" />
                    </Text>
                    <div className={styles["config-subitem-price"]}>
                        <Button
                            variant="apheleia-text"
                            className={clsx(!financeQuoteId ? styles["no-text-underline"] : "")}
                            onClick={() =>
                                showModalHelper(MODAL_FINANCE_CALCULATOR, { step: CarConfigConfigureSection.Summary })
                            }
                        >
                            {!financeQuoteId ? (
                                <div className={styles["open-finance-calculator"]}>
                                    <Icon variant="calculator" sizing="sm" />
                                    <CarConfigLabel label="carConfigOpenFinanceCalculator" />
                                </div>
                            ) : (
                                <CarConfigLabel label="carConfigEditFinancePlan" />
                            )}
                        </Button>
                    </div>
                </li>

                {financeQuoteId ? (
                    <>
                        <li className={styles["config-subitem"]}>
                            <div className={styles["config-subitem-title"]}>
                                <Text tag="h4" variant="apheleia-body">
                                    <strong>
                                        <CarConfigLabel label="carConfigMontlyTotal" />
                                        {info?.productName && `: ${info.productName}`}
                                    </strong>
                                </Text>
                            </div>
                            <div className={styles["config-subitem-price"]}>
                                <Text tag="h4" variant="apheleia-body">
                                    <Price price={price} />
                                </Text>
                            </div>
                        </li>
                        {monthlyRateItems.labels
                            ?.filter(({ key }) => key !== "price")
                            .map((row) =>
                                row ? (
                                    <li className={styles["config-subitem"]} key={`${row.key}-${row.value}`}>
                                        <div className={styles["config-subitem-title"]}>
                                            <Text tag="h4" variant="apheleia-body">
                                                <span dangerouslySetInnerHTML={{ __html: row.value }} />
                                            </Text>
                                        </div>
                                    </li>
                                ) : null,
                            )}
                        <li className={`${styles["config-subitem"]} ${styles["config-subitem-finance"]}`}>
                            <Text tag="h4" variant="apheleia-body">
                                {showFinanceDisclaimerInline && info.disclaimer?.value ? (
                                    <span dangerouslySetInnerHTML={{ __html: info.disclaimer.value }} />
                                ) : (
                                    <Button
                                        variant="apheleia-text"
                                        className={styles["finance-terms-link"]}
                                        onClick={() => {
                                            trackOrderOverviewFinanceTerms();
                                            showModalHelper(MODAL_FINANCE_DISCLAIMER, {});
                                        }}
                                    >
                                        <CarConfigLabel label="carConfigReadCoreFinanceTerms" />
                                    </Button>
                                )}
                            </Text>
                        </li>
                    </>
                ) : (
                    carConfigFinanceInfo && (
                        <Text variant="apheleia-body" tag="p" className={styles["finance-insurance-info"]}>
                            {carConfigFinanceInfo}
                        </Text>
                    )
                )}
            </ul>
            <SummaryServices />
        </section>
    );
};

export default SummaryFinance;
